<template>
  <div id="barchart" :style="{ width: width + 'px', height: myHeight + 'px'}">
    <v-layout align-start justify-start row fill-height>
      <v-flex>
        <v-layout align-start justify-start column fill-height>
          <v-flex>
            <v-tooltip right>
              <div class="sortButton" @click="mySort()" slot="activator"></div>
              <span>Change sorting</span>
              <ul>
              <li v-for="(i, index) in sortItems" :key="'sortItem_' + index">
                <b v-if="i.text === selectedSortItem"><u>{{ i.text }}</u></b>
                <span v-else>{{ i.text }}</span>
              </li>
              </ul>
            </v-tooltip>
          </v-flex>
          <v-flex>
            <v-tooltip right>
              <div class="downloadButton" @click="download()" slot="activator"></div>
              <span>Download PNG of bar chart </span>
            </v-tooltip>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex>
        <v-layout align-start justify-start column fill-height>
          <svg class='grouped-bar-chart'></svg>
          <tooltip :visible="ttvisible" :top="tttop" :left="ttleft" :title="tttitle" :subtitle="ttsubtitle" :value="ttvalue"></tooltip>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import * as d3 from 'd3'
import { saveSvgAsPng } from 'save-svg-as-png'
import ToolTip from './Tooltip'
import { calcTooltipPosition } from '../util'

export default {
  name: 'bar-chart',

  props: ['chartData', 'width', 'height', 'selectedSorting'],

  components: {
    'tooltip': ToolTip
  },

  data () {
    return {
      showLoupeTooltip: false,
      calcTooltipPosition: calcTooltipPosition,
      cWidth: null,
      cHeight: null,
      oHeight: null,
      chart: null,
      overview: null,
      x0Scale: null,
      x1Scale: null,
      yScale: null,
      x0ScaleOverview: null,
      x1ScaleOverview: null,
      yScaleOverview: null,
      xzoom: null,
      xAxis: null,
      yAxis: null,
      xAxisOverview: null,
      brush: null,
      ttvisible: false,
      tttop: null,
      ttleft: null,
      tttitle: null,
      ttsubtitle: null,
      ttvalue: null,
      yAxisGenerator: null,
      sortItems: [
        { text: 'A-Z' },
        { text: 'Z-A' },
        { text: '0-9' },
        { text: '9-0' }
      ]
    }
  },
  computed: {
    selectedSortItem: {
      get () {
        return this.selectedSorting
      },
      set (selectedSorting) {
        this.$emit('input:selectedSorting', selectedSorting)
      }
    },
    myHeight: {
      get () {
        return this.height - 30
      }
    },
    myWidth: {
      get () {
        return this.width - 40
      }
    }
  },
  methods: {
    toggleLoupeTooltip () {
      this.showLoupeTooltip = !this.showLoupeTooltip
    },
    createChart () {
      this.oHeight = this.myHeight / 20
      let margin = {top: 25, right: 10, bottom: 150, left: 70}
      let marginOverview = {top: this.myHeight - margin.bottom + 100, right: margin.right, bottom: 5, left: margin.left}

      let domains = this.getDomains()

      this.cWidth = this.myWidth - margin.left - margin.right - 40
      this.cHeight = this.myHeight - margin.top - margin.bottom

      let svg = d3.select('.grouped-bar-chart')
        .attr('width', this.myWidth)
        .attr('height', this.myHeight)

      this.chart = svg.append('g')
        .attr('class', 'bars')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)

      this.chart.append('defs').append('clipPath')
        .attr('id', 'clip')
        .append('rect')
        .attr('width', this.cWidth)
        .attr('height', this.cHeight + margin.bottom)

      this.overview = svg.append('g')
        .attr('class', 'overview')
        .attr('id', 'overview')
        .attr('transform', `translate(${marginOverview.left}, ${marginOverview.top})`)
        .on('mousemove', this.brushMouseMove)
        .on('mouseout', this.brushMouseOut)

      this.x0Scale = d3.scaleBand().domain(domains.x0).range([0, this.cWidth]).paddingInner(0.1).paddingOuter(1)
      this.x1Scale = d3.scaleBand().domain(domains.x1).range([0, this.x0Scale.bandwidth()]).padding(0.05)
      this.yScale = d3.scaleLinear().domain([0, domains.y]).range([this.cHeight, 0])

      this.x0ScaleOverview = d3.scaleBand().domain(domains.x0).range([0, this.cWidth]).paddingInner(0.1).paddingOuter(1)
      this.x1ScaleOverview = d3.scaleBand().domain(domains.x1).range([0, this.x0ScaleOverview.bandwidth()]).padding(0.05)
      this.yScaleOverview = d3.scaleLinear().domain([0, domains.y]).range([this.oHeight, 0])

      this.xZoom = d3.scaleLinear().domain([0, this.cWidth]).range([0, this.cWidth])

      this.xAxis = svg.append('g')
        .attr('clip-path', 'url(#clip)')
        .attr('class', 'axis axis--x')
        .attr('transform', `translate(${margin.left}, ${margin.top + this.cHeight})`)
        .call(d3.axisBottom(this.x0Scale))

      this.xAxis.selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.3em')
        .attr('transform', 'rotate(-45)')

      this.yAxis = svg.append('g')
        .attr('class', 'axis axis--y')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)
        // TODO: Which ones of these lines are redundant?
        // .call(d3.axisLeft(this.yScale))

      this.xAxisOverview = svg.append('g')
        .attr('class', 'axis axis--x')
        .attr('transform', `translate(${margin.left}, ${marginOverview.top + this.oHeight})`)
        .call(d3.axisBottom(this.x0ScaleOverview))

      this.xAxisOverview.selectAll('text').remove()

      this.brush = d3.brushX()
        .extent([[0, 0], [this.cWidth, this.oHeight]])
        .on('brush', this.brushed)

      this.overview.append('g')
        .attr('class', 'brush')
        .call(this.brush)
        .call(this.brush.move, this.x0ScaleOverview.range())

      this.overview.on('mouseover', this.toggleLoupeTooltip)
      this.overview.on('mouseout', this.toggleLoupeTooltip)
    },
    updateChart () {
      let that = this
      let domains = this.getDomains()
      let color = d3.scaleOrdinal(d3.schemeCategory10)

      // this.selectedSortItem = 'A-Z'
      this.x0Scale = this.x0Scale.domain(domains.x0)
      this.x1Scale = this.x1Scale.domain(domains.x1).range([0, this.x0Scale.bandwidth()])
      this.yScale = this.yScale.domain([0, domains.y])
      this.x0ScaleOverview = this.x0ScaleOverview.domain(domains.x0)
      this.x1ScaleOverview = this.x1ScaleOverview.domain(domains.x1).range([0, this.x0ScaleOverview.bandwidth()])
      this.yScaleOverview = this.yScaleOverview.domain([0, domains.y])

      this.xAxis.transition().call(d3.axisBottom(this.x0Scale).tickFormat(this.truncate))
      this.yAxis.transition().call(d3.axisLeft(this.yScale).tickFormat(d3.format('.2s')))
      this.xAxisOverview.transition().call(d3.axisBottom(this.x0ScaleOverview))

      this.xAxisOverview.selectAll('text').remove()

      this.xAxis.selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.3em')
        .attr('transform', 'rotate(-45)')

      // remove old chart
      this.chart.selectAll('g').remove()
      this.overview.selectAll('.bar').remove()

      // compose chart data; exclude rows with null values
      let chartData = d3.nest().key(function (d) { return d[domains.firstDim] })
        .entries(this.chartData.data)

      this.chart.append('g')
        .attr('clip-path', 'url(#clip)')
        .selectAll('g')
        .data(chartData)
        .enter().append('g')
        .attr('transform', d => 'translate(' + this.x0Scale(d.key) + ',0)')
        .attr('class', 'bargroups')
        .selectAll('.bar')
        .data(d => d.values)
        .enter().append('rect')
        .attr('class', 'bar')
        .attr('x', d => this.x1Scale(d[domains.secondDim]))
        .attr('y', d => this.yScale(d.value))
        .attr('width', this.x1Scale.bandwidth())
        .attr('height', d => this.cHeight - this.yScale(d.value))
        .attr('fill', d => color(d[domains.secondDim]))
        .on('mousemove', function (d) { that.mousemove([d[domains.firstDim], d[domains.secondDim], d.value]) })
        .on('mouseout', this.mouseout)
        .on('click', d => { that.$emit('click:item', { firstDimCode: d[domains.firstDim], complementCode: d[domains.secondDim] }) })

      // remove labels if smaller than font-size (10px)
      if (this.x1Scale.bandwidth() < 10) {
        this.xAxis.selectAll('text').style('display', 'none')
      } else {
        this.xAxis.selectAll('text').style('display', null)
      }

      this.overview.append('g')
        .selectAll('g')
        .data(chartData)
        .enter().append('g')
        .attr('transform', d => 'translate(' + this.x0ScaleOverview(d.key) + ',0)')
        .attr('class', 'bargroups')
        .selectAll('.bar')
        .data(d => d.values)
        .enter().append('rect')
        .attr('class', 'bar')
        .attr('x', d => this.x1ScaleOverview(d[domains.secondDim]))
        .attr('y', d => this.yScaleOverview(d.value))
        .attr('width', this.x1ScaleOverview.bandwidth())
        .attr('height', d => this.oHeight - this.yScaleOverview(d.value))
        .attr('fill', d => color(d[domains.secondDim]))

      // remove old legend
      this.chart.select('.legend').remove()

      // show legend only if at least 1 topic is present
      if (domains.x1[0] !== undefined) {
        let legend = this.chart.selectAll('.legend')
          .data(domains.x1)
          .enter().append('g')
          .attr('class', 'legend')
          .attr('transform', (d, i) => 'translate(0,' + i * 20 + ')')
          .style('opacity', '1')

        legend.append('rect')
          .attr('x', this.cWidth - 18)
          .attr('width', 18)
          .attr('height', 18)
          .style('fill', d => color(d))

        legend.append('text')
          .attr('x', this.cWidth - 24)
          .attr('y', 9)
          .attr('dy', '.35em')
          .style('text-anchor', 'end')
          .text(d => this.getFullName(d))
      }

      this.sort()
    },
    resize () {
      d3.select(this.$el).selectAll('g').remove()
      this.createChart()
      this.updateChart()
    },
    getDomains () {
      let firstDim = this.chartData.group_by[0]
      let secondDim = this.chartData.group_by[1]

      let x0 = this.chartData.data.map(function (d) { return d[firstDim] })
      let x1 = [...new Set(this.chartData.data.map(function (d) { return d[secondDim] }))]
      let y = d3.max(this.chartData.data, function (d) { return +d.value })

      // disable numerical sorting if GroupedBarChart
      let isGrouped = (secondDim !== undefined)
      this.sortItems.forEach(function (d) {
        if (d.disabled !== undefined) {
          d.disabled = isGrouped
        }
      })

      return {
        firstDim: firstDim,
        secondDim: secondDim,
        x0: x0.sort((a, b) => d3.ascending(this.getFullName(a), this.getFullName(b))),
        x1: x1.sort((a, b) => d3.ascending(this.getFullName(a), this.getFullName(b))),
        y: y
      }
    },
    mousemove (d) {
      let chart = document.getElementById('barchart')
      let mouseX = d3.mouse(chart)[0]
      let mouseY = d3.mouse(chart)[1]
      let tt = calcTooltipPosition({x: mouseX, y: mouseY}, this.$el.getBoundingClientRect(), 15, 15)
      this.ttleft = tt.x
      this.tttop = tt.y
      this.tttitle = this.getFullName(d[0])
      this.ttsubtitle = this.getFullName(d[1])
      this.ttvalue = d[2]
      this.ttvisible = true
    },
    mouseout () {
      this.ttvisible = false
    },
    brushMouseMove () {
      this.tttitle = 'meta tooltip'
      this.ttsubtitle = ''
      this.ttvalue = 'zoom into the chart by resizing and repositioning the area highlighted below: grab left or right edge to resize; click and drag sideways to move'
      this.ttvisible = true
    },
    brushMouseOut () {
      this.ttvisible = false
    },
    brushed () {
      if (d3.event.selection === null) return
      let domains = this.getDomains()
      let s = d3.event.selection
      let originalRange = this.xZoom.range()

      this.xZoom = this.xZoom.domain([s[0], s[1]])
      this.x0Scale = this.x0Scale.range([this.xZoom(originalRange[0]), this.xZoom(originalRange[1])])
      this.x1Scale = this.x1Scale.range([0, this.x0Scale.bandwidth()])

      this.chart.selectAll('.bargroups')
        .attr('transform', d => 'translate(' + this.x0Scale(d.key) + ',0)')

      this.chart.selectAll('.bar')
        .attr('x', d => this.x1Scale(d[domains.secondDim]))
        .attr('width', this.x1Scale.bandwidth())

      this.xAxis.call(d3.axisBottom(this.x0Scale).tickFormat(this.truncate))

      if (this.x1Scale.bandwidth() < 10) {
        this.xAxis.selectAll('text').style('display', 'none')
      } else {
        this.xAxis.selectAll('text').style('display', null)
      }
    },
    sort () {
      let that = this
      let domains = this.getDomains()
      let x0Domain = domains.x0
      let transition = this.chart.transition().duration(500)
      let transitionOverview = this.overview.transition().duration(500)

      // only needed for numerical sorting
      let dataMap = d3.map(this.chartData.data, function (d) {
        return d[domains.firstDim]
      })

      switch (this.selectedSortItem) {
        case 'A-Z':
          x0Domain = x0Domain.sort(function (a, b) { return d3.ascending(that.getFullName(a), that.getFullName(b)) })
          break
        case 'Z-A':
          x0Domain = x0Domain.sort(function (a, b) { return d3.descending(that.getFullName(a), that.getFullName(b)) })
          break
        case '0-9':
          x0Domain = x0Domain.sort(function (a, b) { return d3.ascending(+dataMap.get(a).value, +dataMap.get(b).value) })
          break
        case '9-0':
          x0Domain = x0Domain.sort(function (a, b) { return d3.descending(+dataMap.get(a).value, +dataMap.get(b).value) })
          break
      }

      let x0ScaleSorted = this.x0Scale.domain(x0Domain)
      let x0ScaleOverviewSorted = this.x0ScaleOverview.domain(x0Domain)

      this.xAxis.transition().call(d3.axisBottom(x0ScaleSorted).tickFormat(this.truncate))
      this.xAxisOverview.transition().call(d3.axisBottom(x0ScaleOverviewSorted))

      transition.selectAll('.bargroups')
        .attr('transform', d => 'translate(' + x0ScaleSorted(d.key) + ',0)')

      transitionOverview.selectAll('.bargroups')
        .attr('transform', d => 'translate(' + x0ScaleOverviewSorted(d.key) + ',0)')
    },
    truncate (d) {
      if (d === undefined || d === null) return
      let label = this.getFullName(d)
      return (label.length > 10) ? label.substring(0, 10) + '...' : label
    },
    getFullName (d) {
      return this.$store.state.geoMap.has(d) ? this.$store.state.geoMap.get(d) : this.$store.state.topicMap.has(d) ? this.$store.state.topicMap.get(d).text : d
    },
    download () {
      let svg = d3.select('.grouped-bar-chart').node()
      saveSvgAsPng(svg, 'KNOWMAK_barchart.png', {height: this.myHeight, width: this.myWidth, backgroundColor: 'white'})
    },
    mySort () {
      let index = (this.sortItems.findIndex(sortItem => sortItem.text === this.selectedSortItem) + 1) % this.sortItems.length
      this.selectedSortItem = this.sortItems[index].text
    }
  },
  watch: {
    width: 'resize',
    height: 'resize',
    chartData: {
      handler: 'updateChart'
      // deep: true
    },
    selectedSortItem: 'sort'
  },
  mounted () {
    this.createChart()
    this.updateChart()
  }
}
</script>

<style>
rect {
  /*fill: steelblue;*/
}

text {
  fill: black;
  font: 10px sans-serif;
}

.downloadButton {
  background-image: url(data:image/svg+xml;utf8;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pgo8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMTYuMC4wLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogNi4wMCBCdWlsZCAwKSAgLS0+CjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+CjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiBpZD0iQ2FwYV8xIiB4PSIwcHgiIHk9IjBweCIgd2lkdGg9IjE2cHgiIGhlaWdodD0iMTZweCIgdmlld0JveD0iMCAwIDQzMy41IDQzMy41IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCA0MzMuNSA0MzMuNTsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8Zz4KCTxnIGlkPSJmaWxlLWRvd25sb2FkIj4KCQk8cGF0aCBkPSJNMzk1LjI1LDE1M2gtMTAyVjBoLTE1M3YxNTNoLTEwMmwxNzguNSwxNzguNUwzOTUuMjUsMTUzeiBNMzguMjUsMzgyLjV2NTFoMzU3di01MUgzOC4yNXoiIGZpbGw9IiMwMDAwMDAiLz4KCTwvZz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8Zz4KPC9nPgo8L3N2Zz4K);
  background-size: 16px 16px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin: 20px 0 6px 24px;

}

.sortButton {
  /* background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJub25lIiBkPSJNMCAwaDI0djI0SDBWMHptMCAwaDI0djI0SDBWMHptLjc1Ljc1aDIyLjV2MjIuNUguNzV6Ii8+PHBhdGggZD0iTTE0Ljk0IDQuNjZoLTQuNzJsMi4zNi0yLjM2em0tNC42OSAxNC43MWg0LjY2bC0yLjMzIDIuMzN6TTYuMSA2LjI3TDEuNiAxNy43M2gxLjg0bC45Mi0yLjQ1aDUuMTFsLjkyIDIuNDVoMS44NEw3Ljc0IDYuMjdINi4xem0tMS4xMyA3LjM3bDEuOTQtNS4xOCAxLjk0IDUuMThINC45N3ptMTAuNzYgMi41aDYuMTJ2MS41OWgtOC41M3YtMS4yOWw1LjkyLTguNTZoLTUuODh2LTEuNmg4LjN2MS4yNmwtNS45MyA4LjZ6Ii8+PC9zdmc+);
  */
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMyAxOGg2di0ySDN2MnpNMyA2djJoMThWNkgzem0wIDdoMTJ2LTJIM3YyeiIvPjxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiIGZpbGw9Im5vbmUiLz48L3N2Zz4=);
  background-size: 24px 24px;
  cursor: pointer;
  width: 16px;
  height: 24px;
  margin: 35px 0 15px 24px;

}
</style>



// WEBPACK FOOTER //
// src/components/BarChart.vue